/**
 * Loading, pagination, selection, and live polling for manual ingestion runs.
 */

import { useCallback, useEffect, useState } from "react";
import { clinicalTrialsService } from "../api/clinicalTrialsService";
import type { JobStatusResponse } from "../api/clinicalTrialsApiTypes";

const PAGE_SIZE = 20;
const POLL_INTERVAL_MS = 4000;
const ACTIVE_STATUSES: ReadonlyArray<JobStatusResponse["status"]> = [
  "queued",
  "running",
];

/** State and actions consumed by the manual ingestion history UI. */
export interface ManualJobHistoryState {
  jobs: JobStatusResponse[];
  selectedJob: JobStatusResponse | null;
  loading: boolean;
  loadingMore: boolean;
  detailLoading: boolean;
  error: string | null;
  detailError: string | null;
  hasMore: boolean;
  totalCount: number;
  resumingRunId: string | null;
  refresh: () => Promise<void>;
  loadMore: () => Promise<void>;
  selectRun: (runId: string) => Promise<JobStatusResponse | null>;
  trackNewRun: (runId: string) => Promise<void>;
  resumeRun: (runId: string) => Promise<boolean>;
}

/**
 * Merge history pages without duplicating a run.
 *
 * @param current - Runs already loaded
 * @param incoming - Newly fetched runs
 * @returns De-duplicated runs retaining newest-first order
 */
function mergeRuns(
  current: JobStatusResponse[],
  incoming: JobStatusResponse[],
): JobStatusResponse[] {
  const byId = new Map(current.map((run) => [run.run_id, run]));
  incoming.forEach((run) => byId.set(run.run_id, run));
  return Array.from(byId.values()).sort((left, right) =>
    (right.started_at || "").localeCompare(left.started_at || ""),
  );
}

/**
 * Manage manual ingestion history and poll only the selected active run.
 *
 * @returns Manual history state and actions
 */
export function useManualJobHistory(): ManualJobHistoryState {
  const [jobs, setJobs] = useState<JobStatusResponse[]>([]);
  const [selectedJob, setSelectedJob] = useState<JobStatusResponse | null>(
    null,
  );
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [detailLoading, setDetailLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [detailError, setDetailError] = useState<string | null>(null);
  const [hasMore, setHasMore] = useState(false);
  const [totalCount, setTotalCount] = useState(0);
  const [resumingRunId, setResumingRunId] = useState<string | null>(null);

  /**
   * Reload the newest history page.
   *
   * @returns Promise resolved after the request completes
   */
  const refresh = useCallback(
    async (preserveRun?: JobStatusResponse): Promise<void> => {
      setLoading(true);
      setError(null);
      try {
        const response = await clinicalTrialsService.getRunHistory(
          0,
          PAGE_SIZE,
        );
        setJobs(
          preserveRun &&
            !response.items.some((run) => run.run_id === preserveRun.run_id)
            ? mergeRuns(response.items, [preserveRun])
            : response.items,
        );
        setTotalCount(response.total_count);
        setHasMore(response.has_more);
        setSelectedJob((current) =>
          current
            ? response.items.find((run) => run.run_id === current.run_id) ||
              current
            : current,
        );
      } catch (err) {
        setError((err as Error)?.message || "Failed to load job history");
      } finally {
        setLoading(false);
      }
    },
    [],
  );

  useEffect(() => {
    void refresh();
  }, [refresh]);

  /**
   * Append the next history page.
   *
   * @returns Promise resolved after the request completes
   */
  const loadMore = useCallback(async (): Promise<void> => {
    if (loadingMore || !hasMore) return;
    setLoadingMore(true);
    setError(null);
    try {
      const response = await clinicalTrialsService.getRunHistory(
        jobs.length,
        PAGE_SIZE,
      );
      setJobs((current) => mergeRuns(current, response.items));
      setTotalCount(response.total_count);
      setHasMore(response.has_more);
    } catch (err) {
      setError((err as Error)?.message || "Failed to load older jobs");
    } finally {
      setLoadingMore(false);
    }
  }, [hasMore, jobs.length, loadingMore]);

  /**
   * Select a run and refresh its authoritative details.
   *
   * @param runId - Run identifier to open
   * @returns Promise resolved after detail loading completes
   */
  const selectRun = useCallback(
    async (runId: string): Promise<JobStatusResponse | null> => {
      setSelectedJob(jobs.find((run) => run.run_id === runId) || null);
      setDetailLoading(true);
      setDetailError(null);
      try {
        const run = await clinicalTrialsService.getRunStatus(runId);
        setSelectedJob(run);
        setJobs((current) => mergeRuns(current, [run]));
        return run;
      } catch (err) {
        setDetailError((err as Error)?.message || "Failed to load run details");
        return null;
      } finally {
        setDetailLoading(false);
      }
    },
    [jobs],
  );

  /**
   * Select a newly accepted run and refresh the history list.
   *
   * @param runId - Newly accepted run identifier
   * @returns Promise resolved after selection and history refresh
   */
  const trackNewRun = useCallback(
    async (runId: string): Promise<void> => {
      const run = await selectRun(runId);
      await refresh(run || undefined);
    },
    [refresh, selectRun],
  );

  /**
   * Resume the same interrupted parent and keep it selected.
   *
   * @param runId - Interrupted durable parent identifier
   * @returns Whether resume dispatch succeeded
   */
  const resumeRun = useCallback(async (runId: string): Promise<boolean> => {
    setResumingRunId(runId);
    setDetailError(null);
    try {
      const run = await clinicalTrialsService.resumeRun(runId);
      setSelectedJob(run);
      setJobs((current) => mergeRuns(current, [run]));
      return true;
    } catch (err) {
      setDetailError((err as Error)?.message || "Failed to resume run");
      return false;
    } finally {
      setResumingRunId(null);
    }
  }, []);

  useEffect(() => {
    if (!selectedJob || !ACTIVE_STATUSES.includes(selectedJob.status)) return;
    const interval = window.setInterval(async () => {
      try {
        const run = await clinicalTrialsService.getRunStatus(
          selectedJob.run_id,
        );
        setSelectedJob(run);
        setJobs((current) => mergeRuns(current, [run]));
      } catch (err) {
        setDetailError((err as Error)?.message || "Failed to refresh run");
      }
    }, POLL_INTERVAL_MS);
    return () => window.clearInterval(interval);
  }, [selectedJob]);

  return {
    jobs,
    selectedJob,
    loading,
    loadingMore,
    detailLoading,
    error,
    detailError,
    hasMore,
    totalCount,
    resumingRunId,
    refresh,
    loadMore,
    selectRun,
    trackNewRun,
    resumeRun,
  };
}
